import {
  Fragment,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useState,
} from 'react';

import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, type SxProps, type Theme, Typography } from '@mui/material';

import {
  cardSx,
  dividerSx,
  headerTextSx,
  mutedTextSx,
  titleSx,
  withClickable,
} from './analytics.styles';
import { StudentDataModal } from './StudentDataModal';
import { type StudentDataHandle } from './useStudentData';

const { viewData: viewDataStrings } = appStrings.modules.coachingAnalytics;

type AnalyticsCardProps = Readonly<{
  title: string;
  subtitle?: string;
  /** Caveats and definitions, shown on the header info icon. Falsy entries drop out. */
  info?: readonly InfoNote[];
  /** Heading of the info modal when it should say more than the card title. */
  infoTitle?: string;
  // Rendered between the header and its divider.
  belowHeader?: ReactNode;
  id?: string;
  onClick?: () => void;
  /** Student list behind the card; adds the header "View data" action and its modal. */
  viewData?: StudentDataHandle;
  children: ReactNode;
}>;

export const AnalyticsCard = ({
  title,
  subtitle,
  info,
  infoTitle,
  belowHeader,
  id,
  onClick,
  viewData,
  children,
}: AnalyticsCardProps) => (
  <Box id={id} onClick={onClick} sx={withClickable(cardSx, onClick)}>
    <Box sx={cardHeaderRowSx}>
      <Box sx={headerTextSx}>
        <Box sx={titleRowSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {title}
          </Typography>
          {viewData ? <CardInfoIcon info={info} title={title} heading={infoTitle} /> : null}
        </Box>
        {subtitle ? (
          <Typography variant="text-sm-regular" sx={mutedTextSx}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {viewData ? (
        <ViewDataTrigger handle={viewData} />
      ) : (
        <CardInfoIcon info={info} title={title} heading={infoTitle} />
      )}
    </Box>
    {belowHeader}
    <Box sx={dividerSx} />
    <Box sx={cardBodySx}>{children}</Box>
  </Box>
);

export const ViewDataTrigger = ({ handle }: Readonly<{ handle: StudentDataHandle }>) => (
  <>
    <Button
      id={`${handle.id}-view-data-btn`}
      hierarchy="Secondary gray"
      size="sm"
      sx={viewDataTriggerSx}
      startIcon={<AppIcon iconName="Table" diameter="16px" color="currentColor" />}
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        handle.openAll();
      }}
    >
      {viewDataStrings.trigger}
    </Button>
    <Box sx={modalGuardSx} onClick={(event) => event.stopPropagation()}>
      <StudentDataModal {...handle.modal} />
    </Box>
  </>
);

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** A paragraph of the info modal, or a pre-laid-out element for anything richer. */
export type InfoNote = string | ReactElement | false | null | undefined;

/** Info icon, made interactive only when the card has notes to show. */
export const CardInfoIcon = ({
  info,
  title,
  heading = title,
}: Readonly<{
  info?: readonly InfoNote[];
  title: string;
  heading?: string;
}>) => {
  const [isOpen, setIsOpen] = useState(false);
  const notes = (info ?? []).filter(
    (note): note is string | ReactElement => typeof note === 'string' || isValidElement(note),
  );
  const icon = (
    <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
  );

  if (notes.length === 0) return icon;

  return (
    <>
      <Box
        id={`analytics-card-info-${slug(title)}`}
        sx={infoTriggerSx}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        {icon}
      </Box>
      {/* The modal portals out of the card, but its clicks still bubble the React tree into
          the card's onClick, so they stop here. display:contents keeps the wrapper out of layout. */}
      <Box sx={modalGuardSx} onClick={(event) => event.stopPropagation()}>
        <InfoModal
          open={isOpen}
          onClose={() => setIsOpen(false)}
          width="lg"
          dialogSx={infoDialogSx}
          hideCircleImage
          headerContent={<Typography variant="text-lg-semibold">{heading}</Typography>}
          bodyContent={
            <Box sx={infoListSx}>
              {notes.map((note, index) =>
                typeof note === 'string' ? (
                  <Typography key={note} variant="text-md-regular" sx={mutedTextSx}>
                    {note}
                  </Typography>
                ) : (
                  <Fragment key={index}>{note}</Fragment>
                ),
              )}
            </Box>
          }
        />
      </Box>
    </>
  );
};

const infoTriggerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  padding: backrs.spacing['spacing-xs'],
  margin: `-${backrs.spacing['spacing-xs']}`,
  borderRadius: backrs.radius['radius-full'],
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  },
});

export const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  minWidth: 0,
});

const viewDataTriggerSx: SxProps<Theme> = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const modalGuardSx: SxProps<Theme> = {
  display: 'contents',
};

const INFO_DIALOG_WIDTH = 396;

const infoDialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    maxWidth: INFO_DIALOG_WIDTH,
    maxHeight: 'calc(75vh - 48px)',
  },
};

const infoListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

/**
 * Cards in a row share the tallest card's height, so the body centres in
 * whatever space it is given rather than leaving the surplus below it.
 */
const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-2xl'],
  flex: 1,
  width: '100%',
  minWidth: 0,
});

const cardHeaderRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});
